import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Input, MultiSelectField, SelectField, TextArea } from '../containers/Form';
import { closeModal } from '../actions/modal';
import { notificationActions } from '../components/Notification/redux/actions';
import Wizard from '../containers/Modal/Wizard';
import Strings from '../Strings';
import actions from '../pages/SuperUser/SuperuserHealthCenter/redux/actions';
import {
  CREATE_SUPERUSER_HEALTH_ALERT_RULE,
  UPDATE_SUPERUSER_HEALTH_ALERT_RULE,
} from '../pages/SuperUser/SuperuserHealthCenter/redux/constants';
import {
  buildAlertRuleDetectorConfig,
  FAILED_EVENTS_COUNT_DETECTOR_KEY,
} from '../pages/SuperUser/SuperuserHealthCenter/alertRulesUtils';

const getThresholdValueInputValue = value => (value ?? '').toString();

const isThresholdValueMissingOrInvalid = value => value === '' || Number.isNaN(parseFloat(value));

export const EDIT_ALERT_RULE_MODAL_NAME = 'edit-alert-rule-modal';

const MAX_DESCRIPTION_LENGTH = 500;

// Detector Keys
export const DETECTOR_KEYS = [
  { label: Strings.healthMonitorAlerts.detectors.smsFailureRate, value: 'sms_failure_rate' },
  { label: Strings.healthMonitorAlerts.detectors.eventFailureRate, value: 'event_failure_rate' },
  { label: Strings.healthMonitorAlerts.detectors.failedEventsCount, value: 'failed_events_count' },
];

// Threshold operators
export const THRESHOLD_OPERATORS = [
  { label: Strings.healthMonitorAlerts.operators.greaterThan, value: 'gt' },
  { label: Strings.healthMonitorAlerts.operators.greaterThanOrEqual, value: 'gte' },
  { label: Strings.healthMonitorAlerts.operators.lessThan, value: 'lt' },
  { label: Strings.healthMonitorAlerts.operators.lessThanOrEqual, value: 'lte' },
];

// Severity levels
export const SEVERITY_LEVELS = [
  { label: Strings.healthMonitorAlerts.severityLevels.info, value: 'info' },
  { label: Strings.healthMonitorAlerts.severityLevels.warning, value: 'warning' },
  { label: Strings.healthMonitorAlerts.severityLevels.critical, value: 'critical' },
];

export const WINDOW_PRESET_OPTIONS = [
  { label: Strings.healthMonitorAlerts.timeWindows.last1Hour, value: 'last_1h' },
  { label: Strings.healthMonitorAlerts.timeWindows.last24Hours, value: 'last_24h' },
  { label: Strings.healthMonitorAlerts.timeWindows.last7Days, value: 'last_7d' },
  { label: Strings.healthMonitorAlerts.timeWindows.last28Days, value: 'last_28d' },
  { label: Strings.healthMonitorAlerts.timeWindows.last30Days, value: 'last_30d' },
];

function FieldHelp({ label, content }) {
  return (
    <div className="right-top-text">
      <span
        className="icon"
        data-tooltip-content={content}
        data-tooltip-id="tooltip"
        data-tooltip-place="right"
        aria-label={Strings.formatString(Strings.healthMonitorAlerts.fieldHelp, label)}
      >
        ?
      </span>
    </div>
  );
}

FieldHelp.propTypes = {
  label: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
};

function EditRuleModal(props) {
  const {
    data,
    closeModalWithNextAction,
    createAlertRule,
    getFailedEventsFormBundle,
    updateAlertRule,
    showNotification,
  } = props;

  const isEdit = !!data?.rule;
  const rule = data?.rule;

  // Form state
  const [description, setDescription] = React.useState(rule?.description || '');
  const [detectorKey, setDetectorKey] = React.useState(rule?.detector_key || '');
  const [thresholdValue, setThresholdValue] = React.useState(getThresholdValueInputValue(rule?.threshold_value));
  const [comparison, setComparison] = React.useState(rule?.threshold_operator || 'gt');
  const [severity, setSeverity] = React.useState(rule?.severity || 'warning');
  const [active, setActive] = React.useState(rule?.active !== false);
  const [windowPreset, setWindowPreset] = React.useState(
    rule?.detector_config?.window_preset || WINDOW_PRESET_OPTIONS[1].value,
  );
  const [failCategoriesExclude, setFailCategoriesExclude] = React.useState(
    rule?.detector_config?.fail_categories_exclude || [],
  );
  const [failCategoriesInclude, setFailCategoriesInclude] = React.useState(
    rule?.detector_config?.fail_categories_include || [],
  );
  const [capIdPattern, setCapIdPattern] = React.useState(rule?.detector_config?.cap_id_pattern || '');
  const [availableFailCategories, setAvailableFailCategories] = React.useState([]);
  const [failCategoriesLoadState, setFailCategoriesLoadState] = React.useState('idle');

  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightInvalidFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (detectorKey !== FAILED_EVENTS_COUNT_DETECTOR_KEY) return;

    let isCurrent = true;
    setFailCategoriesLoadState('loading');
    getFailedEventsFormBundle()
      .then(resp => {
        if (!isCurrent) return;
        const categories = resp?.response?.fail_categories;
        if (!Array.isArray(categories)) {
          setFailCategoriesLoadState('error');
          return;
        }
        setAvailableFailCategories(categories);
        setFailCategoriesLoadState('loaded');
      })
      .catch(() => {
        isCurrent && setFailCategoriesLoadState('error');
      });

    return () => {
      isCurrent = false;
    };
  }, [detectorKey, getFailedEventsFormBundle]);

  const failCategoryOptions = React.useMemo(
    () =>
      [...new Set([...availableFailCategories, ...failCategoriesInclude, ...failCategoriesExclude])].map(category => ({
        value: category,
        label: category,
      })),
    [availableFailCategories, failCategoriesExclude, failCategoriesInclude],
  );

  // Validation
  React.useEffect(() => {
    const errors = [];

    if (!description.trim()) {
      errors.push({
        property: 'description',
        propertyName: Strings.healthMonitorAlerts.descriptionLabel,
        missing: true,
      });
    }

    if (description.trim().length > MAX_DESCRIPTION_LENGTH) {
      errors.push({
        property: 'description',
        propertyName: Strings.healthMonitorAlerts.descriptionLabel,
        errors: [Strings.formatString(Strings.healthMonitorAlerts.descriptionTooLong, MAX_DESCRIPTION_LENGTH)],
        missing: false,
      });
    }

    if (!detectorKey) {
      errors.push({ property: 'detectorKey', propertyName: Strings.healthMonitorAlerts.metricType, missing: true });
    }

    if (isThresholdValueMissingOrInvalid(thresholdValue)) {
      errors.push({
        property: 'thresholdValue',
        propertyName: Strings.healthMonitorAlerts.thresholdValue,
        missing: true,
      });
    }

    if (!windowPreset || !WINDOW_PRESET_OPTIONS.some(option => option.value === windowPreset)) {
      errors.push({ property: 'windowPreset', propertyName: Strings.healthMonitorAlerts.windowPreset, missing: true });
    }

    if (!comparison) {
      errors.push({
        property: 'comparison',
        propertyName: Strings.healthMonitorAlerts.thresholdOperator,
        missing: true,
      });
    }

    if (!severity) {
      errors.push({ property: 'severity', propertyName: Strings.healthMonitorAlerts.severity, missing: true });
    }

    setValidationErrors(errors);
  }, [description, detectorKey, thresholdValue, windowPreset, comparison, severity, active]);

  // Submit handler
  const onSubmit = () => {
    setIsSaving(true);

    const ruleData = {
      detector_key: detectorKey,
      detector_config: buildAlertRuleDetectorConfig({
        detectorKey,
        windowPreset,
        failCategoriesExclude,
        failCategoriesInclude,
        capIdPattern,
      }),
      description: description.trim(),
      threshold_value: parseFloat(thresholdValue),
      comparison,
      severity,
      active,
    };

    const action = isEdit ? updateAlertRule(rule.id, ruleData) : createAlertRule(ruleData);

    action.then(resp => {
      const expectedType = isEdit ? UPDATE_SUPERUSER_HEALTH_ALERT_RULE : CREATE_SUPERUSER_HEALTH_ALERT_RULE;

      if (resp?.type !== `${expectedType}/result`) {
        showNotification(
          resp?.response?.data?.error?.message || Strings.healthMonitorAlerts.ruleSaveFailed,
          5000,
          true,
        );
        setIsSaving(false);
        return;
      }

      showNotification(isEdit ? Strings.healthMonitorAlerts.ruleUpdated : Strings.healthMonitorAlerts.ruleCreated);
      closeModalWithNextAction();
    });
  };

  const pages = [
    {
      id: 'edit-alert-rule-page',
      title: isEdit ? Strings.healthMonitorAlerts.editRule : Strings.healthMonitorAlerts.createRule,
      content: (
        <>
          <TextArea
            name="description"
            id="description"
            label={Strings.healthMonitorAlerts.descriptionLabel}
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={MAX_DESCRIPTION_LENGTH}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'description')?.errors || []}
          />

          <div className="control-with-top-right-text">
            <SelectField
              name="detectorKey"
              id="detectorKey"
              label={Strings.healthMonitorAlerts.detectorKey}
              value={detectorKey}
              onChange={o => setDetectorKey(o.value)}
              data={DETECTOR_KEYS}
              isRequired
              highlightInvalid={highlightInvalidFields}
            />
            <FieldHelp
              label={Strings.healthMonitorAlerts.detectorKey}
              content={Strings.healthMonitorAlerts.detectorKeyHelp}
            />
          </div>

          <div className="horizontal-flex">
            <SelectField
              name="comparison"
              id="comparison"
              label={Strings.healthMonitorAlerts.operator}
              value={comparison}
              onChange={setComparison}
              data={THRESHOLD_OPERATORS}
              isRequired
            />

            <Input
              name="value"
              id="value"
              label={Strings.healthMonitorAlerts.thresholdValue}
              type="number"
              value={thresholdValue}
              onChange={e => setThresholdValue(e.target.value)}
              isRequired
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={validationErrors.find(v => v.property === 'thresholdValue')?.errors || []}
            />
          </div>

          <div className="horizontal-flex">
            <SelectField
              name="severity"
              id="severity"
              label={Strings.healthMonitorAlerts.severity}
              value={severity}
              onChange={o => setSeverity(o.value)}
              data={SEVERITY_LEVELS}
              isRequired
            />

            <SelectField
              name="windowPreset"
              id="windowPreset"
              label={Strings.healthMonitorAlerts.timeWindow}
              value={windowPreset}
              onChange={o => setWindowPreset(o.value)}
              data={WINDOW_PRESET_OPTIONS}
              isRequired
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={validationErrors.find(v => v.property === 'windowPreset')?.errors || []}
            />
          </div>

          <SelectField
            name="active"
            id="active"
            label={Strings.healthMonitorAlerts.status}
            value={active}
            onChange={o => setActive(o.value)}
            data={[
              { label: Strings.healthMonitorAlerts.active, value: true },
              { label: Strings.healthMonitorAlerts.inactive, value: false },
            ]}
            isRequired
          />

          {detectorKey === FAILED_EVENTS_COUNT_DETECTOR_KEY && (
            <>
              <div className="control-with-top-right-text">
                <MultiSelectField
                  name="failCategoriesInclude"
                  id="failCategoriesInclude"
                  label={Strings.healthMonitorAlerts.failureCategoriesIncludeOptional}
                  value={failCategoriesInclude}
                  onChange={options => setFailCategoriesInclude(options.map(option => option.value))}
                  data={failCategoryOptions.map(option => ({
                    ...option,
                    isDisabled: failCategoriesExclude.includes(option.value),
                  }))}
                  placeholder={Strings.healthMonitorAlerts.allCategories}
                  noOptionsMessage={() => (
                    failCategoriesLoadState === 'error'
                      ? Strings.healthMonitorAlerts.failureCategoriesLoadFailed
                      : Strings.healthMonitorAlerts.noCategoriesAvailable
                  )}
                  checkboxFirst
                  closeMenuOnSelect={false}
                />
                <FieldHelp
                  label={Strings.healthMonitorAlerts.failureCategoriesInclude}
                  content={Strings.healthMonitorAlerts.failureCategoriesIncludeHelp}
                />
              </div>
              <div className="control-with-top-right-text">
                <MultiSelectField
                  name="failCategoriesExclude"
                  id="failCategoriesExclude"
                  label={Strings.healthMonitorAlerts.failureCategoriesExcludeOptional}
                  value={failCategoriesExclude}
                  onChange={options => setFailCategoriesExclude(options.map(option => option.value))}
                  data={failCategoryOptions.map(option => ({
                    ...option,
                    isDisabled: failCategoriesInclude.includes(option.value),
                  }))}
                  placeholder={Strings.healthMonitorAlerts.noExcludedCategories}
                  noOptionsMessage={() => (
                    failCategoriesLoadState === 'error'
                      ? Strings.healthMonitorAlerts.failureCategoriesLoadFailed
                      : Strings.healthMonitorAlerts.noCategoriesAvailable
                  )}
                  checkboxFirst
                  closeMenuOnSelect={false}
                />
                <FieldHelp
                  label={Strings.healthMonitorAlerts.failureCategoriesExclude}
                  content={Strings.healthMonitorAlerts.failureCategoriesExcludeHelp}
                />
              </div>
              <div className="control-with-top-right-text">
                <Input
                  name="capIdPattern"
                  id="capIdPattern"
                  label={Strings.healthMonitorAlerts.capIdPatternOptional}
                  value={capIdPattern}
                  onChange={e => setCapIdPattern(e.target.value)}
                  placeholder={Strings.healthMonitorAlerts.capIdPatternPlaceholder}
                />
                <FieldHelp
                  label={Strings.healthMonitorAlerts.capIdPattern}
                  content={Strings.healthMonitorAlerts.capIdPatternHelp}
                />
              </div>
            </>
          )}
        </>
      ),
      nextButton: {
        text: isEdit ? Strings.healthMonitorAlerts.updateRule : Strings.healthMonitorAlerts.createRuleButton,
      },
      emptyFieldsCount: validationErrors.filter(e => e.missing).length,
      canGoNext: !isSaving && validationErrors.length === 0,
    },
  ];

  return (
    <Wizard
      name={EDIT_ALERT_RULE_MODAL_NAME}
      pages={pages}
      onSubmit={onSubmit}
      onNextButtonHover={e => setHighlightInvalidFields(e)}
    />
  );
}

EditRuleModal.propTypes = {
  data: PropTypes.shape({
    rule: PropTypes.object,
  }),
  onCancel: PropTypes.func,
  closeModalWithNextAction: PropTypes.func.isRequired,
  createAlertRule: PropTypes.func,
  getFailedEventsFormBundle: PropTypes.func.isRequired,
  updateAlertRule: PropTypes.func,
  showNotification: PropTypes.func,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onCancel: () => dispatch(closeModal(EDIT_ALERT_RULE_MODAL_NAME)),
  createAlertRule: data => dispatch(actions.createAlertRule(data)),
  getFailedEventsFormBundle: () => dispatch(actions.getFailedEventsFormBundle()),
  updateAlertRule: (ruleId, data) => dispatch(actions.updateAlertRule(ruleId, data)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  closeModalWithNextAction: () => {
    dispatch(closeModal(EDIT_ALERT_RULE_MODAL_NAME));
    if (ownProps.data?.onSuccess) ownProps.data.onSuccess();
  },
});

export default connect(null, mapDispatchToProps)(EditRuleModal);
